import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { usePagination } from 'alova/client'
import { useState } from 'react'
import { Spin, message } from 'antd'
import dayjs from 'dayjs'
import NiceModal from '@ebay/nice-modal-react'
import styles from './StatisticComparePage.module.less'
import { commonUtils } from '@/helpers/commonUtils'
import useProductType from '@/helpers/commonHooks/useProductType'
import { getProductDashboardLink } from '@/router/pageConst'
import ASearch from '@/components/A/ASearch/ASearch'
import AIcon from '@/components/A/AIcon/AIcon'
import { api } from '@/api'
import { compare } from '@/api/compare/compare'
import CommonModal from '@/components/Modals/CommonModal/CommonModal'

const PAGE_SIZE = 10

function StatisticComparePage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { productType } = useProductType()
  const [keyword, setKeyword] = useState('')
  const {
    data: reportList,
    loading,
    total,
    reload,
    page,
    update,
  } = usePagination(
    (pageNo, pageSize) => api.compare.compareReportList({
      pageNo,
      pageSize,
      keyword,
      projectCategory: productType,
    }),
    {
      initialPage: 1,
      initialPageSize: PAGE_SIZE,
      data: (res) => res.objects,
      total: (res) => res.totalCount,
      append: true,
      watchingStates: [keyword, productType],
    }
  )

  const handleDelete = (report: compare.CompareReport) => {
    NiceModal.show(CommonModal, {
      title: t('dashboard.dataComparisonPage.deleteTitle', { defaultValue: '删除对比报告' }),
      content: t('dashboard.dataComparisonPage.deleteConfirm', {
        defaultValue: '确认删除该对比报告？删除后不可恢复。',
      }),
      confirmText: t('dashboard.projectPage.delete', { defaultValue: '删除' }),
      cancelText: t('dashboard.projectPage.cancel', { defaultValue: '取消' }),
      onConfirm: () => {
        api.compare.compareReportDelete(report.id).send().then(() => {
          message.success(t('dashboard.projectPage.message.deleteSuccess', { defaultValue: '删除成功' }))
          reload()
        })
      },
    })
  }

  const goInfo = (reportId: number) => {
    navigate(`${getProductDashboardLink(productType, 'statisticCompareInfo')}?reportId=${reportId}`)
  }

  const hasNext = page * PAGE_SIZE < (total || 0)
  const emptyText = keyword
    ? t('dashboard.dataComparisonPage.searchEmptyText', { defaultValue: '未搜索到对比报告' })
    : t('dashboard.dataComparisonPage.emptyText', {
      defaultValue: '当前暂无对比报告，请先选择项目进行对比',
    })

  return (
    <div className={styles.statisticComparePage} data-testid='statistic-compare-page'>
      <div className={styles.statisticComparePageContent}>
        <section className={styles.createCompareSection}>
          <SectionTitle>{t('dashboard.dataComparisonPage.createCompare', { defaultValue: '新建对比' })}</SectionTitle>
          <p className={styles.description}>
            {t('dashboard.dataComparisonPage.createCompareDesc', {
              defaultValue: '支持多选项目进行对比，最多选择 10 个项目。',
            })}
          </p>
          <button
            className={styles.compareButton}
            type='button'
            data-testid='statistic-compare-start-btn'
            onClick={() => navigate(getProductDashboardLink(productType, 'statisticCompareStart'))}
          >
            {t('dashboard.dataComparisonPage.startCompare', { defaultValue: '开始对比' })}
          </button>
        </section>

        <div className={styles.divider} />

        <section className={styles.reportSection}>
          <div className={styles.reportHeader}>
            <SectionTitle>{t('dashboard.dataComparisonPage.compareReport', { defaultValue: '对比报告' })}</SectionTitle>
            <ASearch
              className={styles.searchInput}
              testId='statistic-compare-search-input'
              placeholder={t('dashboard.dataComparisonPage.searchPlaceholder', {
                defaultValue: '搜索对比报告名称',
              })}
              allowClear
              onSearch={(value) => setKeyword(value ? `${value}` : '')}
            />
          </div>

          {loading && reportList.length === 0 ? (
            <div className={styles.loadingBox}><Spin /></div>
          ) : null}
          {!loading && reportList.length === 0 ? (
            <div className={styles.emptyState}>
              <img
                className={styles.emptyImage}
                src={commonUtils.getImagePath('common/common-empty.png')}
                alt=''
              />
              <p className={styles.emptyText}>
                {emptyText}
              </p>
            </div>
          ) : null}
          {reportList.length > 0 ? (
            <>
              <div className={styles.reportList}>
                {reportList.map((report) => (
                  <article
                    key={report.id}
                    className={styles.reportCard}
                    data-testid={`statistic-compare-report-card-${report.id}`}
                  >
                    <button
                      type='button'
                      className={styles.reportMain}
                      onClick={() => goInfo(report.id)}
                    >
                      <span className={styles.reportName}>{report.reportName}</span>
                      <span className={styles.reportMeta}>
                        {t('dashboard.dataComparisonPage.projectCount', {
                          count: report.projectCount,
                          defaultValue: '{{count}} 个项目',
                        })}
                        {' · '}
                        {dayjs(report.createdAt * 1000).format('YYYY-MM-DD HH:mm')}
                      </span>
                    </button>
                    <button
                      type='button'
                      className={styles.deleteButton}
                      data-testid={`statistic-compare-report-delete-btn-${report.id}`}
                      onClick={() => handleDelete(report)}
                      aria-label={t('dashboard.projectPage.delete', { defaultValue: '删除' })}
                    >
                      <AIcon type='icon-line-global_delete' width={14} height={14} />
                    </button>
                  </article>
                ))}
              </div>
              {hasNext ? (
                <button
                  type='button'
                  className={styles.loadMoreButton}
                  onClick={() => update({ page: page + 1 })}
                  disabled={loading}
                >
                  {loading
                    ? t('common.loading', { defaultValue: '加载中' })
                    : t('dashboard.projectPage.loadMore', { defaultValue: '加载更多' })}
                </button>
              ) : null}
            </>
          ) : null}
        </section>
      </div>
    </div>
  )
}

function SectionTitle(props: { children: React.ReactNode }) {
  return <h2 className={styles.sectionTitle}>{props.children}</h2>
}

export default StatisticComparePage
